<template>
   <v-container class="ResetPassword-container mx-auto">
    <LoginTopbar />
    <v-row class="ResetPassword-content">
      <v-col class="d-flex justify-center">
         <AppCard :elevation="0"
                  class="ResetPassword-card"
        >
          <v-row class="mb-3">
            <v-col>
              <h2>{{ $t('login.views.ResetPassword.title') }}</h2>
            </v-col>
          </v-row>
           <v-row>
            <v-col>
              <AppTextField v-model.trim="login.email"
                            :label="$t('login.views.ResetPassword.email')"
                            type="email"
                            class="ResetPassword-input"
                            :error-messages="emailErrors"
                            maxlength="255"
                            @blur="validateEmail"
                            @keypress.enter="prepareResetPassword"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <AppTextField v-model="login.password"
                            :type="displayPassword ? 'text' : 'password'"
                            :label="$t('login.views.ResetPassword.password')"
                            class="ResetPassword-input"
                            :error-messages="passwordErrors"
                            maxlength="255"
                            @blur="validatePassword"
                            @keypress.enter="prepareResetPassword"
              >
                <template #append-inner>
                    <AppButton icon
                               @click="displayPassword = !displayPassword"
                    >
                        <app-material-icon v-if="displayPassword" name="visibility"></app-material-icon>
                        <app-material-icon v-else name="visibility_off"></app-material-icon>
                    </AppButton>
                </template>
              </AppTextField>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <PasswordRulesDisplay
                :password="login.password"
                :is-not-previous-password="isNotPreviousPassword"
              />
            </v-col>
          </v-row>
          <v-row class="mt-5">
            <v-col>
              <AppTextField v-model="login.passwordConfirmation"
                            :type="displayConfirmPassword ? 'text' : 'password'"
                            :label="$t('login.views.ResetPassword.confirmPassword')"
                            class="ResetPassword-input"
                            :error-messages="passwordConfirmErrors"
                            maxlength="255"
                            @blur="validatePasswordConfirmation"
                            @keypress.enter="prepareResetPassword"
              >
                <template #append-inner>
                    <AppButton icon
                               @click="displayConfirmPassword = !displayConfirmPassword"
                    >
                        <app-material-icon v-if="displayConfirmPassword" name="visibility"></app-material-icon>
                        <app-material-icon v-else name="visibility_off"></app-material-icon>
                    </AppButton>
                </template>
              </AppTextField>
            </v-col>
          </v-row>
          <v-col class="text-center mt-3">
                <AppButton color="primary"
                           :loading="loading"
                           @click="prepareResetPassword"
                >
                  {{$t('login.views.ResetPassword.resetPassword')}}
                </AppButton>
            </v-col>
          </AppCard>
          </v-col>
          </v-row>
          <div class="d-flex justify-center mt-5">
            <BackToClosd />
          </div>
  </v-container>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { createNamespacedHelpers } from 'vuex'


import AppCard from '@/common/AppCard.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import PasswordRulesDisplay from '@/common/PasswordRulesDisplay.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { Login } from '@/models/login.model'
import { RESET_PASSWORD } from '@/store/modules/login/action_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import BackToClosd from '../BackToClosd.vue'
import LoginTopbar from '../LoginTopbar.vue'

const { mapState, mapActions } = createNamespacedHelpers('login')

export default {
  name: 'ResetPassword',
  components: {
    AppButton,
    LoginTopbar,
    AppCard,
    AppTextField,
    BackToClosd,
    PasswordRulesDisplay,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      login: {
        email: { required, postmarkEmail },
        password: {
          required,
        },
        passwordConfirmation: {
          required,
          sameAs: sameAs(this.login.password),
        },
      },
    }
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      login: new Login('', '', false, '', '', '', this.token),
      displayPassword: false,
      displayConfirmPassword: false,
      isNotPreviousPassword: undefined,
    }
  },
  computed: {
    ...mapState(['loading']),
    emailErrors () {
      return formFieldErrors(
        this.v$.login.email,
        [
          ['required', this.$t('login.views.ResetPassword.rules.email.required', { fieldName: this.$t('login.views.ResetPassword.email') })],
          ['postmarkEmail', this.$t('login.views.ResetPassword.rules.email.valid')],
        ],
      )
    },
    passwordErrors () {
      return formFieldErrors(
        this.v$.login.password,
        [
          ['required', this.$t('login.views.ResetPassword.rules.password.required', { fieldName: this.$t('login.views.ResetPassword.password') })],
        ],
      )
    },
    passwordConfirmErrors () {
      return formFieldErrors(
        this.v$.login.passwordConfirmation,
        [
          ['sameAs', this.$t('login.views.ResetPassword.rules.confirmPassword.equals', { fieldName: this.$t('login.views.ResetPassword.confirmPassword') })],
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.ResetPassword.confirmPassword') })],
        ],
      )
    },
  },
  methods: {
    ...mapActions([RESET_PASSWORD]),
    validateEmail () {
      this.v$.login.email.$touch()
    },
    validatePassword () {
      this.v$.login.password.$touch()
    },
    validatePasswordConfirmation () {
      this.v$.login.passwordConfirmation.$touch()
    },
    async prepareResetPassword () {
      if (this.loading) {
        return
      }
      this.v$.login.$touch()
      if (!this.v$.login.$invalid) {
        try {
          await this.RESET_PASSWORD(this.login)
        } catch (error) {
          const errorCode = error?.response?.data?.errorCode
          let msgError = ''
          if (errorCode === 'ERR_INCORRECT_CREDENTIALS') {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.incorrectCredentials')
          } else if (errorCode === 'ERR_EXPIRED_RESET_TOKEN') {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.expiredToken')
          } else if (errorCode === 'ERR_PASSWORD_NOT_FRESH') {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.passwordNotFresh')
          } else if (errorCode === 'ERR_PASSWORD_NOT_MATCHING') {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.passwordNotMatching')
          } else if (errorCode === 'ERR_PASSWORD_NOT_VALID') {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.passwordNotValid')
          } else {
            msgError = this.$t('login.views.ResetPassword.resetPasswordError.generic')
          }
          toast.error(msgError)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "vuetify/settings";

  .ResetPassword-container {
    max-width: 1100px;
    max-height: 840px;
  }
  .ResetPassword-divider{
    margin-left: -36px;
    margin-right: -36px
  }
  .ResetPassword-content{
    margin-top: 10%;
  }
  .ResetPassword-card {
    box-shadow: 0 0 10px 0 rgba(94, 86, 105, 0.1) !important;
    border: 1px solid #bdbdbd;
    padding: 25px 35px;
    width: 538px;
  }
  .ResetPassword-validation-check{
    font-size: 30px;
  }
  :deep(.theme--light.v-btn.v-btn--disabled.v-btn--has-bg) {
    background-color: #ffe3e1 !important;
  }
  :deep(.ResetPassword-input .v-input__slot) {
    background-color: #fff;
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')}{
    .ResetPassword-content{
      margin-top: 0;
    }
  }
</style>
